/**
 * Hand-drawn barbershop line art (own drawings): scissors, comb, razor,
 * mustache, … All strokes use currentColor, so they follow the theme.
 * Built with DOM APIs — no innerHTML (see eslint rule).
 */
const NS = 'http://www.w3.org/2000/svg';

type Attrs = Record<string, string | number>;
export type SvgNode = [tag: string, attrs: Attrs, ...children: SvgNode[]];

function el(node: SvgNode): SVGElement {
  const [tag, attrs, ...children] = node;
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  for (const c of children) e.appendChild(el(c));
  return e;
}

export function svg(viewBox: string, nodes: SvgNode[], attrs: Attrs = {}): SVGSVGElement {
  return el(['svg', { xmlns: NS, viewBox, 'aria-hidden': 'true', focusable: 'false', ...attrs }, ...nodes]) as SVGSVGElement;
}

const line = { fill: 'none', stroke: 'currentColor', 'stroke-width': 1.8, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
const icon = (nodes: SvgNode[], size = 22, cls = 'icon') => svg('0 0 24 24', [['g', line, ...nodes]], { width: size, height: size, class: cls });

// --- 24px icons ------------------------------------------------------------------

export const ICONS = {
  scissors: (): SvgNode[] => [
    ['circle', { cx: 6, cy: 7, r: 2.6 }],
    ['circle', { cx: 6, cy: 17, r: 2.6 }],
    ['path', { d: 'M8.2 8.6 20 15.6M8.2 15.4 20 8.4' }],
    ['circle', { cx: 13.9, cy: 12, r: 0.7, fill: 'currentColor' }],
  ],
  comb: (): SvgNode[] => [
    ['rect', { x: 3, y: 6, width: 18, height: 4.5, rx: 1.2 }],
    ['path', { d: Array.from({ length: 10 }, (_, i) => `M${(4.6 + i * 1.65).toFixed(2)} 10.5V${i % 2 ? 16.4 : 18}`).join('') }],
  ],
  razor: (): SvgNode[] => [
    ['path', { d: 'M3.5 14.5 14.5 4.8c1.9-1.6 4.7-.2 4.1 2.2L9.5 15.6Z' }],
    ['path', { d: 'M9.5 15.6 5.2 20.4', 'stroke-width': 2.6 }],
    ['circle', { cx: 9.5, cy: 15.6, r: 0.9, fill: 'currentColor' }],
  ],
  mustache: (): SvgNode[] => [
    ['path', {
      d: 'M12 11.2c-1.3-2-4-2.6-5.9-1.2-1 .8-1.2 2.1-2.4 2.3-.8.1-1.4-.4-1.7-1 .1 2.6 2.2 4.3 4.8 4 2.2-.2 3.9-1.6 5.2-2.7 1.3 1.1 3 2.5 5.2 2.7 2.6.3 4.7-1.4 4.8-4-.3.6-.9 1.1-1.7 1-1.2-.2-1.4-1.5-2.4-2.3-1.9-1.4-4.6-.8-5.9 1.2Z',
      fill: 'currentColor', stroke: 'none',
    }],
  ],
  droplet: (): SvgNode[] => [
    ['path', { d: 'M12 3.5s-6 6.6-6 10.6a6 6 0 0 0 12 0c0-4-6-10.6-6-10.6Z' }],
    ['path', { d: 'M9.4 14.6a2.7 2.7 0 0 0 2.1 2.4' }],
  ],
  sparkle: (): SvgNode[] => [['path', { d: 'M12 3l1.9 5.4L19.5 10l-5.6 1.7L12 17l-1.9-5.3L4.5 10l5.6-1.6Z' }]],
  clock: (): SvgNode[] => [['circle', { cx: 12, cy: 12, r: 8.5 }], ['path', { d: 'M12 7.5V12l3 2' }]],
  phone: (): SvgNode[] => [['path', { d: 'M6.6 3.5h2.6l1.4 4-1.9 1.3a11 11 0 0 0 6.5 6.5l1.3-1.9 4 1.4v2.6a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.7a2 2 0 0 1 2-2.2Z' }]],
  pin: (): SvgNode[] => [['path', { d: 'M12 21s-6.5-5.7-6.5-11a6.5 6.5 0 0 1 13 0c0 5.3-6.5 11-6.5 11Z' }], ['circle', { cx: 12, cy: 10, r: 2.3 }]],
  check: (): SvgNode[] => [['path', { d: 'M5 12.5l4.2 4.2L19 7', 'stroke-width': 2.4 }]],
  users: (): SvgNode[] => [
    ['circle', { cx: 9, cy: 8, r: 3 }],
    ['path', { d: 'M3.5 19a5.5 5.5 0 0 1 11 0' }],
    ['circle', { cx: 17, cy: 9, r: 2.4 }],
    ['path', { d: 'M15.6 14.2a4.5 4.5 0 0 1 4.9 4.8' }],
  ],
  calendar: (): SvgNode[] => [['rect', { x: 3.5, y: 5, width: 17, height: 15.5, rx: 2.2 }], ['path', { d: 'M3.5 9.5h17M8 3v4M16 3v4' }]],
  arrowDown: (): SvgNode[] => [['path', { d: 'M12 5v14M6 13l6 6 6-6' }]],
  sun: (): SvgNode[] => [['circle', { cx: 12, cy: 12, r: 4 }], ['path', { d: 'M12 2.5v2M12 19.5v2M4.6 4.6 6 6M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4' }]],
  sunset: (): SvgNode[] => [['path', { d: 'M7 15a5 5 0 0 1 10 0M3 15h18M5 19h14M12 4v4M5.6 8.6l1.4 1.4M18.4 8.6 17 10' }]],
  moon: (): SvgNode[] => [['path', { d: 'M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10Z' }]],
} as const;

export type IconName = keyof typeof ICONS;

export function iconEl(name: IconName, size = 22, cls = 'icon'): SVGSVGElement {
  return icon(ICONS[name](), size, cls);
}

/** Picks a drawing for a service from its id/name (haircut → scissors, beard → mustache, …). */
export function serviceIcon(service: { id: string; name: string; name_de?: string }): IconName {
  const s = `${service.id} ${service.name} ${service.name_de ?? ''}`.toLowerCase();
  if (/kid|kind/.test(s)) return 'sparkle';
  if (/shave|rasur|razor|rasier/.test(s)) return 'razor';
  if (/colou?r|färb|farb|tön|blond/.test(s)) return 'droplet';
  if (/beard|bart/.test(s) && !/cut|schnitt|hair|haar/.test(s)) return 'mustache';
  if (/style|styling|wash|wäsche|föhn|comb/.test(s)) return 'comb';
  return 'scissors';
}

// --- Hero composition ------------------------------------------------------------

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Rotates a scissors half around the pivot now and then (a little "snip"). */
const snip = (angle: number, delay: string): SvgNode[] =>
  reducedMotion()
    ? []
    : [['animateTransform', {
        attributeName: 'transform', type: 'rotate', additive: 'sum',
        values: `0 4.2 0; ${angle} 4.2 0; 0 4.2 0; 0 4.2 0`, keyTimes: '0; 0.06; 0.12; 1',
        dur: '4.5s', begin: delay, repeatCount: 'indefinite',
      }]];

/** Big open scissors over a comb, with a small razor — the shop's hero artwork. */
export function heroArt(): SVGSVGElement {
  const s = { fill: 'none', stroke: 'currentColor', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
  const teeth = Array.from({ length: 17 }, (_, i) => `M${8 + i * 9.6} 26V${i % 2 ? 58 : 68}`).join('');
  return svg('0 0 260 240', [
    // comb
    ['g', { ...s, transform: 'translate(34 150) rotate(-14)', 'stroke-width': 3.2 },
      ['rect', { x: 0, y: 0, width: 176, height: 26, rx: 7, fill: 'currentColor', 'fill-opacity': 0.14 }],
      ['path', { d: teeth }],
    ],
    // razor
    ['g', { ...s, transform: 'translate(176 168) rotate(-32) scale(2.6)', 'stroke-width': 1.3, class: 'art-accent' },
      ['path', { d: 'M3.5 14.5 14.5 4.8c1.9-1.6 4.7-.2 4.1 2.2L9.5 15.6Z', fill: 'currentColor', 'fill-opacity': 0.18 }],
      ['path', { d: 'M9.5 15.6 5.2 20.4', 'stroke-width': 2.2 }],
    ],
    // scissors (two halves that snip)
    ['g', { ...s, transform: 'translate(122 86) rotate(24)', 'stroke-width': 4 },
      ['g', {},
        ['circle', { cx: -62, cy: -22, r: 19, class: 'art-accent', fill: 'currentColor', 'fill-opacity': 0.16 }],
        ['path', { d: 'M-45 -13 L80 20', 'stroke-width': 6.5 }],
        ...snip(-7, '1s'),
      ],
      ['g', {},
        ['circle', { cx: -62, cy: 22, r: 19, class: 'art-accent', fill: 'currentColor', 'fill-opacity': 0.16 }],
        ['path', { d: 'M-45 13 L80 -20', 'stroke-width': 6.5 }],
        ...snip(7, '1s'),
      ],
      ['circle', { cx: 4.2, cy: 0, r: 4.5, fill: 'currentColor', stroke: 'none' }],
    ],
    // a few cut hairs
    ['g', { ...s, 'stroke-width': 2, opacity: 0.55 },
      ['path', { d: 'M214 52c4 3 5 8 3 12M228 70c2 4 1 9-2 11M206 82c3 2 4 6 3 9' }],
    ],
  ], { class: 'hero-art', width: 260, height: 240 });
}

/** Success animation: scissors closing a few times, then a check. */
export function snipBadge(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'snip-badge';
  const s = { fill: 'none', stroke: 'currentColor', 'stroke-linecap': 'round', 'stroke-width': 4 };
  const half = (sign: 1 | -1): SvgNode => ['g', {},
    ['circle', { cx: -36, cy: sign * -13, r: 11 }],
    ['path', { d: `M-26 ${sign * -8} L44 ${sign * 12}`, 'stroke-width': 5 }],
    ...(reducedMotion() ? [] : [['animateTransform', {
      attributeName: 'transform', type: 'rotate', values: `0 2 0; ${sign * 9} 2 0; 0 2 0`, dur: '0.45s', repeatCount: 3,
    }] as SvgNode]),
  ];
  wrap.append(
    svg('-60 -40 120 80', [['g', s, half(1), half(-1), ['circle', { cx: 2, cy: 0, r: 3, fill: 'currentColor', stroke: 'none' }]]], { class: 'snip-scissors', width: 120, height: 80 }),
    icon(ICONS.check(), 30, 'snip-check'),
  );
  return wrap;
}
